import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  TextField,
  Button,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Switch,
} from '@mui/material';
import { Add, Edit, Delete, Refresh as RefreshIcon, Search as SearchIcon } from '@mui/icons-material';
import AlertModal from "../../components/AlertModal";
import { hasWritePermission } from '../../utils/menuFilter';
import ConfirmModal from "../../components/ConfirmModal";

const ProjectType = () => {
  const canWrite = hasWritePermission('master.general-master');
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', status: 'Active' });
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [errors, setErrors] = useState({});

  // Modal alert/confirm states
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertConfig, setAlertConfig] = useState({ title: '', message: '' });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [selectedTypeForDelete, setSelectedTypeForDelete] = useState(null);
  const [statusConfirmOpen, setStatusConfirmOpen] = useState(false);
  const [selectedTypeForStatus, setSelectedTypeForStatus] = useState(null);

  const API_BASE = process.env.REACT_APP_API_BASE_URL;

  // Fetch project types from API
  const fetchProjectTypes = async (silent = false) => {
    if (!silent) setIsLoading(true);
    setApiError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/globalMasterDetails?type=projectType`,
      );
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const result = await response.json();
      const projectTypes = Array.isArray(result.data)
        ? result.data.map((item) => ({
          id: item._id,
          name: item.details?.projectType || item.details?.name || 'Unknown',
          status: item.status === true ? 'Active' : 'Inactive',
          rawStatus: item.status,
        }))
        : [];

      setRows(projectTypes);
    } catch (err) {
      setApiError('Failed to load project types.');
      setRows([]);
    } finally {
      if (!silent) setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProjectTypes();
  }, []);

  const filtered = rows.filter(r => !search || `${r.name} ${r.status}`.toLowerCase().includes(search.toLowerCase()));

  // Validation
  const validateForm = () => {
    const err = {};
    if (!form.name.trim()) err.name = 'Project Type Name is required';
    return err;
  };

  // Duplicate check
  const isDuplicate = () => {
    return rows.some(
      (r) =>
        r.name.toLowerCase() === form.name.trim().toLowerCase() &&
        r.id !== editing?.id,
    );
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ name: '', status: 'Active' });
    setErrors({});
    setDialogOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setForm({ name: row.name, status: row.status });
    setErrors({});
    setDialogOpen(true);
  };

  const saveForm = async () => {
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    if (isDuplicate()) {
      setErrors({ name: 'Project type name already exists' });
      return;
    }

    setIsLoading(true);
    try {
      const payload = {
        type: 'projectType',
        details: {
          projectType: form.name.trim(),
        },
        status: form.status === 'Active',
      };

      let response;
      if (editing) {
        response = await fetch(`${API_BASE}/api/globalMasterDetails/${editing.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        response = await fetch(`${API_BASE}/api/globalMasterDetails`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }

      if (!response.ok) throw new Error('Save failed');

      setAlertConfig({
        title: 'Success',
        message: editing ? 'Project type updated successfully!' : 'Project type added successfully!'
      });
      setAlertOpen(true);
      setDialogOpen(false);
      fetchProjectTypes(true);
    } catch (err) {
      setErrors({ name: 'Failed to save project type.' });
    } finally {
      setIsLoading(false);
    }
  };

  const removeRow = (row) => {
    setSelectedTypeForDelete(row);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedTypeForDelete) return;

    setIsLoading(true);
    setConfirmOpen(false);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedTypeForDelete.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isDeleted: true }),
      });

      if (!response.ok) throw new Error('Delete failed');

      setAlertConfig({ title: 'Success', message: 'Project type deleted successfully!' });
      setAlertOpen(true);
      fetchProjectTypes(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to delete project type.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedTypeForDelete(null);
    }
  };

  const handleConfirmStatusChange = async () => {
    if (!selectedTypeForStatus) return;
    setStatusConfirmOpen(false);
    setIsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/globalMasterDetails/${selectedTypeForStatus.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'projectType',
          details: { projectType: selectedTypeForStatus.name },
          status: selectedTypeForStatus.status !== 'Active'
        }),
      });
      if (!response.ok) throw new Error('Update failed');
      
      setAlertConfig({
        title: 'Success',
        message: `Project type status updated to ${selectedTypeForStatus.status === 'Active' ? 'Inactive' : 'Active'} successfully!`
      });
      setAlertOpen(true);
      fetchProjectTypes(true);
    } catch (err) {
      setAlertConfig({ title: 'Error', message: 'Failed to update status.' });
      setAlertOpen(true);
    } finally {
      setIsLoading(false);
      setSelectedTypeForStatus(null);
    }
  };

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: '#1976d2' }}>Project Type Master Management</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchProjectTypes()}
            disabled={isLoading}
            sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
          >
            Refresh
          </Button>
          {/* {canWrite && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={openAdd}
              disabled={isLoading}
              sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 2 }}
            >
              Add Project Type
            </Button>
          )} */}
        </Box>
      </Box>

      {/* Modal alert/confirm */}
      <AlertModal
        open={alertOpen}
        title={alertConfig.title}
        message={alertConfig.message}
        onClose={() => setAlertOpen(false)}
      />

      <ConfirmModal
        open={confirmOpen}
        title="Confirm Delete"
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleConfirmDelete}
        confirmText="Delete"
        cancelText="Cancel"
        type="error"
      >
        Are you sure you want to delete the project type <strong>{selectedTypeForDelete?.name}</strong>? This action cannot be undone.
      </ConfirmModal>

      <ConfirmModal
        open={statusConfirmOpen}
        title="Confirm Status Change"
        onClose={() => setStatusConfirmOpen(false)}
        onConfirm={handleConfirmStatusChange}
        confirmText="Confirm"
        cancelText="Cancel"
        type="warning"
      >
        Are you sure you want to change the status of <strong>{selectedTypeForStatus?.name}</strong> to <strong>{selectedTypeForStatus?.status === 'Active' ? 'Inactive' : 'Active'}</strong>?
      </ConfirmModal>

      {apiError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {apiError}
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => fetchProjectTypes()}
            sx={{ ml: 2 }}
            size="small"
            variant="outlined"
          >
            Retry
          </Button>
        </Alert>
      )}

      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Search Project Type"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <IconButton size="small" sx={{ mr: 1 }}>
                      <SearchIcon fontSize="small" />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    const count = filtered.length;
                    setAlertConfig({
                      title: 'Search Results',
                      message: count > 0 ? `Found ${count} project types matching your criteria.` : 'No project types found matching your criteria.'
                    });
                    setAlertOpen(true);
                  }}
                  startIcon={<SearchIcon />}
                  sx={{ textTransform: 'none', height: '40px', borderRadius: 2, fontWeight: 600 }}
                >
                  Search
                </Button>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={() => {
                    setSearch('');
                    fetchProjectTypes(true);
                  }}
                  sx={{ textTransform: 'none', height: '40px', borderRadius: 2, fontWeight: 600 }}
                >
                  Reset
                </Button>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          {isLoading && rows.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#536DFE' }}>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>S.No</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Project Type Name</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ color: '#fff', fontWeight: 700, textAlign: 'center' }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.map((r, index) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{r.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={r.status}
                          size="small"
                          color={r.status === 'Active' ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                          <Switch
                            checked={r.status === 'Active'}
                            onChange={() => {
                              setSelectedTypeForStatus(r);
                              setStatusConfirmOpen(true);
                            }}
                            size="small"
                            color="success"
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                  {filtered.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                        {isLoading ? 'Loading...' : 'No types found'}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Project Type' : 'Add Project Type'}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                size="small"
                label="Project Type Name"
                value={form.name}
                onChange={(e) => {
                  if (!editing) {
                    setForm(prev => ({ ...prev, name: e.target.value }));
                    if (errors.name) {
                      setErrors(prev => ({ ...prev, name: '' }));
                    }
                  }
                }}
                error={!!errors.name}
                helperText={errors.name}
                required
                InputProps={{
                  readOnly: !!editing,
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={form.status} label="Status" onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={isLoading}>Cancel</Button>
          <Button variant="contained" onClick={saveForm} disabled={isLoading}>
            {isLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProjectType;